import styled from 'styled-components'
import Switch from 'react-switch'
import { useFeatureIsOn } from '@growthbook/growthbook-react'
import { useContext } from 'react'

import { RecurlyContext } from 'features/checkout'
import { usePlans } from 'hooks/use-plans'
import { LoadingSpinner } from 'components/ui/loading/spinner'

import { palette, fonts, mediaQueries, shadows } from '../../styles/variables'
import { Text as DefaultText } from '../../components/topography'
import { PrimaryButtonElement } from '../../components/buttons/Buttons'
import checkoutBg from '../../styles/assets/checkout/checkout_bg.jpg'
import { translate } from '../../utils/translation'
import { Desktop, Mobile } from '../../utils/media'
import { getUserFreeTrialDays } from '../../utils/userAuth'
import { LoadingWheel } from '../../components/loading/Loading'
import CheckoutPlanSelector from '../../components/selector/CheckoutPlanSelector'
import { growthbookFeatures } from '../../utils/growthbook'

export const Container = styled.div({
  height: '100vh',
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  overflowY: 'scroll',
  alignItems: 'flex-start',
  justifyContent: 'center',
})

export const RadioTick = styled.div({
  position: 'absolute',
  bottom: '0',
  right: '50%',
  transform: 'translate(50%, 50%)',
  zIndex: 2,
  '@media (max-width: 600px)': {
    bottom: '50%',
    right: '10px',
    transform: 'translateY(50%)',
  },
})

export const BlurBackground = () => (
  <div
    style={{
      width: `calc(100% - 15px)`,
      height: '100%',
      position: 'absolute',
    }}
  >
    <img
      src={checkoutBg}
      alt="bg"
      type="image/jpg"
      style={{
        width: '100%',
        position: 'absolute',
        backgroundSize: 'contain',
      }}
    />
    <div
      style={{
        position: 'absolute',
        width: '100%',
        height: '100%',
        background: 'rgba(0, 0, 0, 0.1)',
        backdropFilter: 'blur(8px)',
      }}
    />
  </div>
)

export const Content = styled.div({
  height: 'fit-content',
  maxWidth: '750px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  margin: '10px 0',
  [mediaQueries.mobile]: {
    marginTop: '0',
    padding: '0px',
    marginBottom: '0px',
  },
})

export const Fields = styled.div(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  height: '50px',
  width: '100%',
  marginBottom: '10px',
  '& > div': {
    '&:first-child': {
      flex: 3,
    },
    '&:nth-child(2)': {
      flex: 3,
    },
  },
  '&.card > div': {
    '&:first-child': {
      flex: 3,
      '& .recurly-element': {
        borderRight: 'none',
      },
    },
    '&:nth-child(2)': {
      flex: 1,
      '& .recurly-element': {
        borderRight: 'none',
      },
    },
    '&:nth-child(3)': {
      flex: 1,
    },
    '&:nth-child(4)': {
      flex: 1,
    },
  },
  [mediaQueries.mobile]: {
    height: 'auto',
    flexDirection: 'column',
    marginBottom: '0px',
    '& > div': {
      '&:first-child': {
        flex: 1,
        marginRight: '0',
        '& .recurly-element': {
          borderRight: '1px',
        },
      },
      '&:nth-child(2)': {
        flex: 1,
        marginRight: '0',
      },
      '&:nth-child(3)': {
        flex: 1,
      },
    },
  },
}))

export const Field = styled.div((props) => ({
  display: 'flex',
  position: 'relative',
  alignItems: 'center',
  width: '100%',
  height: '100%',
  minHeight: 50,
  marginBottom: '10px',
  border: props.noBorder ? 'none' : `1px solid ${palette.lightGray20}`,
  '& .recurly-element-inline': {
    width: '100%',
    '& .recurly-element': {
      width: '100%',
      height: '50px',
      borderRadius: '0px',
      marginTop: '0',
      borderColor: palette.lightGray,
      padding: '0 20px',
    },
    '& .recurly-element-year': {
      borderRight: 'none',
    },
  },
  [mediaQueries.mobile]: {
    width: props.mobileWidth || '100%',
  },
}))

export const CardFlagImg = styled.img((props) => ({
  width: 30,
  height: 25,
  marginLeft: 3,
  right: '20px',
  opacity: props.inactive ? 0.5 : 1,
  cursor: props.onClick ? 'pointer' : 'default',
  ...(props.style || {}),
}))

export const Checkout = styled.div(({ isLimitedOffer }) => {
  return {
    display: 'flex',
    position: 'relative',
    width: '90%',
    flexDirection: 'column',
    backgroundColor: palette.white,
    color: palette.darkGray,
    boxShadow: '0 12px 35px 10px rgba(19,23,35,0.04)',
    padding: `${24 + (isLimitedOffer ? 15 : 0)}px 25px 24px`,
    justifyContent: 'flex-start',
    [mediaQueries.mobile]: {
      margin: '16px 0',
      fontSize: '12px',
      padding: `${16 + (isLimitedOffer ? 16 : 0)}px 10px 16px 10px`,
      width: '95%',
      alignItems: 'center',
    },
  }
})

export const Text = styled(DefaultText)((props) => ({
  [mediaQueries.mobile]: {
    fontSize: props.mobileFontSize || '12px',
    marginTop: '10px',
  },
}))

export const Label = styled(Text)(() => ({
  fontSize: '18px',
  margin: '10px 0',
}))

export const PrivacySection = styled.div({
  display: 'block',
  margin: '24px 0 8px 0',
  [mediaQueries.mobile]: {
    margin: '20px 0 40px 0',
  },
})

const SecondaryButtonElement = styled(PrimaryButtonElement)(() => ({
  border: `1px solid ${palette.blue}`,
  backgroundColor: palette.blue,
  color: palette.white,
  textTransform: 'none',
  fontSize: '20px',
  lineHeight: '30px',
  [mediaQueries.mobile]: {
    backgroundColor: palette.blue,
    color: palette.white,
  },
}))

export const SecondaryButton = ({
  onClick,
  width,
  style,
  disabled = false,
  tag,
  type,
}) => (
  <SecondaryButtonElement
    type={type}
    data-testid="submit-checkout-button"
    onClick={onClick}
    width={width}
    style={style}
    disabled={!!disabled}
  >
    {tag}
  </SecondaryButtonElement>
)

export const RightElement = styled.div({
  position: 'absolute',
  right: '10px',
  transform: 'translateY(3px)',
})

export const ThreeDAuthBox = styled.div(({ showThreeDAuth }) => ({
  position: 'absolute',
  width: '490px',
  height: '510px',
  background: palette.white,
  zIndex: 99999999,
  boxShadow: shadows.shadowA,
  top: '250px',
  ...(!showThreeDAuth && { display: 'none' }),
  [mediaQueries.mobile]: {
    width: 'auto',
    height: '400px',
    top: '100px',
  },
}))

const Switcher = styled.div((props) => ({
  display: 'flex',
  width: '100%',
  height: 'auto',
  color: palette.darkGray70,
  margin: '16px 0',
  fontSize: '18px',
  alignItems: 'stretch',
  justifyContent: 'space-around',
  pointerEvents: props.disabled ? 'none' : 'inherit',
  '& .react-switch-handle': {
    left: 0,
  },
  [mediaQueries.mobile]: {
    fontSize: '13px',
    flexDirection: 'column',
  },
}))

const WheelWrapper = styled.div(() => ({
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translate(-50%, -50%)',
}))

const SwitchLabelWrapper = styled.div((props) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'center',
  flex: 1,
  whiteSpace: 'pre',
  border: `1px solid ${props.active ? palette.orange : palette.lightGray30}`,
  padding: '10px 16px',
  position: 'relative',
  cursor: 'pointer',
  [mediaQueries.mobile]: {
    width: '100%',
    backgroundColor: props.active ? palette.white : palette.lightGray30,
    flexDirection: 'row',
  },
}))

const SwitchLabelLeft = styled(SwitchLabelWrapper)((props) => ({
  marginRight: 16,
  minHeight: '100px',
  alignItems: 'flex-end',
  ':after': {
    content: `' '`,
    position: 'absolute',
    right: 0,
    top: '50%',
    width: 0,
    height: 0,
    border: '8px solid transparent',
    borderLeftColor: palette.white,
    borderRight: 0,
    marginTop: '-8px',
    marginRight: '-8px',
  },
  ':before': {
    content: `' '`,
    position: 'absolute',
    right: 0,
    top: '50%',
    width: 0,
    height: 0,
    border: '9px solid transparent',
    borderLeftColor: props.active ? palette.orange : palette.lightGray30,
    borderRight: 0,
    marginTop: '-9px',
    marginRight: '-9px',
  },
  [mediaQueries.mobile]: {
    marginRight: 0,
    ':after': {
      display: 'none',
    },
    ':before': {
      display: 'none',
    },
  },
}))

const SwitchLabelRight = styled(SwitchLabelWrapper)((props) => ({
  marginLeft: 16,
  minHeight: '100px',
  alignItems: 'flex-start',
  ':after': {
    content: `' '`,
    position: 'absolute',
    left: 0,
    top: '50%',
    width: 0,
    height: 0,
    border: '8px solid transparent',
    borderRightColor: palette.white,
    borderLeft: 0,
    marginTop: '-8px',
    marginLeft: '-8px',
  },
  ':before': {
    content: `' '`,
    position: 'absolute',
    left: 0,
    top: '50%',
    width: 0,
    height: 0,
    border: '9px solid transparent',
    borderRightColor: props.active ? palette.orange : palette.lightGray30,
    borderLeft: 0,
    marginTop: '-9px',
    marginLeft: '-9px',
  },
  [mediaQueries.mobile]: {
    marginLeft: 0,
    ':after': {
      display: 'none',
    },
    ':before': {
      display: 'none',
    },
  },
}))

const RestartPlanLabelWrapper = styled.div(() => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'center',
  flex: 1,
  whiteSpace: 'pre',
  border: `1px solid ${palette.orange}`,
  padding: '10px 16px',
  margin: '16px 0',
  fontSize: '18px',
  color: palette.darkGray70,
  [mediaQueries.mobile]: {
    fontSize: '13px',
    backgroundColor: palette.white,
  },
}))

const AIWMonthlyPlanLabelWrapper = styled.div(() => ({
  position: 'relative',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'center',
  flex: 1,
  border: `1px solid ${palette.orange}`,
  padding: '24px 16px 18px',
  margin: '0 0 10px',
  ':after': {
    content: `' '`,
    position: 'absolute',
    right: '50%',
    bottom: 0,
    width: 0,
    height: 0,
    border: '8px solid transparent',
    borderTopColor: palette.white,
    borderBottom: 0,
    marginBottom: '-8px',
    marginRight: '-8px',
  },
  ':before': {
    content: `' '`,
    position: 'absolute',
    right: '50%',
    bottom: 0,
    width: 0,
    height: 0,
    border: '9px solid transparent',
    borderTopColor: palette.orange,
    borderBottom: 0,
    marginBottom: '-9px',
    marginRight: '-9px',
  },
}))

export const PlanSelector = ({
  options,
  value,
  onChangeValue,
  restartPlan,
  promo,
  hasAIWAddon,
  addonLimit,
  tier,
  isCouponLoading,
  isLoading,
}) => {
  const isGrowthbookFeatureOn = useFeatureIsOn(growthbookFeatures.PROMO_OFFER)
  const { isLoading: isRecurlyLoading } = useContext(RecurlyContext)
  usePlans()
  const monthly = options.find(({ plan }) => plan === 'monthly') || {}
  const lifetime = options.find(({ plan }) => plan === 'lifetime') || {}

  if (!value) return null

  if (restartPlan) {
    return (
      <RestartPlanLabelWrapper>
        <DefaultText
          fontFamily={fonts.primaryMedium}
          fontSize="14px"
          lineHeight="24px"
          mobileFontSize="12px"
        >
          {translate('restart_monthly')}
        </DefaultText>
        <DefaultText
          fontFamily={fonts.primaryMedium}
          fontSize="24px"
          lineHeight="24px"
          mobileFontSize="12px"
          color={palette.darkGray}
          margin="5px 0"
        >
          {!monthly?.unitPrice ? (
            <LoadingSpinner className="tw-stroke-orange-500" />
          ) : (
            translate('amount_per_month', monthly?.unitPrice)
          )}
        </DefaultText>
        <DefaultText
          fontFamily={fonts.primary}
          fontSize="13px"
          lineHeight="18px"
        >
          {translate(`cancel_anytime`)}
        </DefaultText>
      </RestartPlanLabelWrapper>
    )
  }

  if (promo) {
    return (
      <RestartPlanLabelWrapper>
        <DefaultText
          fontFamily={fonts.primaryMedium}
          fontSize="24px"
          lineHeight="24px"
          mobileFontSize="12px"
          color={palette.darkGray}
          margin="5px 0"
        >
          {translate('holiday_combo', options[1].unitPrice)}
        </DefaultText>
        <DefaultText
          fontFamily={fonts.primaryMedium}
          fontSize="14px"
          lineHeight="24px"
          mobileFontSize="12px"
        >
          {translate(`${tier}_bf_combo`)}
        </DefaultText>
        <DefaultText
          fontFamily={fonts.primary}
          fontSize="13px"
          lineHeight="18px"
          dangerouslySetInnerHTML={{
            __html: options[1]?.subscriptionDescription,
          }}
        />
        <DefaultText
          fontFamily={fonts.primary}
          fontSize="13px"
          lineHeight="18px"
        >
          {translate(`ubs_atp_combo`)}
        </DefaultText>
      </RestartPlanLabelWrapper>
    )
  }

  if (hasAIWAddon) {
    let title = translate('individual')
    if (tier === 'tier2') title = translate('business')
    if (tier === 'tier3') title = translate('enterprise')

    return (
      <AIWMonthlyPlanLabelWrapper>
        <DefaultText
          fontFamily={fonts.primaryMedium}
          fontSize="40px"
          lineHeight="40px"
          mobileFontSize="30px"
          color={palette.orange}
          margin="0 0 12px"
        >
          {title}
        </DefaultText>
        <DefaultText
          fontFamily={fonts.primary}
          fontSize="14px"
          lineHeight="200%"
          mobileFontSize="12px"
          color={palette.raisinBlack70}
        >
          {translate('ubersuggest_features')}
        </DefaultText>
        <DefaultText
          fontFamily={fonts.primary}
          fontSize="14px"
          lineHeight="200%"
          mobileFontSize="12px"
          color={palette.raisinBlack70}
        >
          {translate('aiw_words_month', addonLimit)}
        </DefaultText>
        <DefaultText
          fontFamily={fonts.primary}
          fontSize="24px"
          lineHeight="36px"
          color={palette.neutral03}
          margin="12px 0 0"
          boldFontSize="40px"
          boldLineHeight="52px"
          boldColor={palette.neutral01}
          dangerouslySetInnerHTML={{
            __html: translate(
              'per_month',
              translate('currency_at_symbol', monthly?.monthlyPrice, ''),
            ),
          }}
        />
      </AIWMonthlyPlanLabelWrapper>
    )
  }

  return isGrowthbookFeatureOn ? (
    <CheckoutPlanSelector
      options={options}
      name="plans"
      selected={value}
      onClick={onChangeValue}
      isLoading={isCouponLoading}
    />
  ) : (
    <Switcher>
      <SwitchLabelLeft
        data-testid="left-box"
        active={value?.value === monthly?.value}
        onClick={() => onChangeValue(options[0])}
      >
        {isCouponLoading ? (
          <WheelWrapper>
            <LoadingWheel width={24} height={24} />
          </WheelWrapper>
        ) : (
          <>
            <Desktop>
              <DefaultText
                data-testid="left-box-title"
                fontFamily={fonts.primaryMedium}
                fontSize="14px"
                lineHeight="24px"
                mobileFontSize="12px"
              >
                {translate(`dropdown_${monthly?.tier}_monthly`)}
              </DefaultText>
              <DefaultText
                data-testid="left-box-price"
                fontFamily={fonts.primary}
                fontSize="14px"
                lineHeight="24px"
                mobileFontSize="12px"
                boldColor={palette.darkGray}
                boldFontFamily={fonts.primaryMedium}
                boldFontSize="24px"
                color={palette.orange}
                dangerouslySetInnerHTML={{
                  __html: `${translate(
                    'currency_at_symbol',
                    monthly?.discountPrice
                      ? monthly?.discountPrice
                      : monthly?.unitPrice,
                    '',
                  )}
                `,
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  margin: '8px 0',
                }}
              />
              {monthly?.discountPercentNumber ? (
                <Text color={palette.orange} fontSize="14px" textAlign="center">
                  {translate(
                    'five_percent_discount',
                    monthly?.discountPercentNumber,
                  )}
                </Text>
              ) : null}
              <DefaultText
                data-testid="left-box-description"
                fontFamily={fonts.primary}
                fontSize="13px"
                lineHeight="18px"
              >
                {translate(`zero_risk_x_day_trial`, getUserFreeTrialDays())}
              </DefaultText>
              <DefaultText
                fontFamily={fonts.primary}
                fontSize="13px"
                lineHeight="18px"
              >
                {translate(`cancel_anytime`)}
              </DefaultText>
            </Desktop>
            <Mobile>
              <div style={{ flex: 2 }}>
                <DefaultText
                  fontFamily={fonts.primaryMedium}
                  fontSize="14px"
                  lineHeight="24px"
                >
                  {translate(`dropdown_${monthly?.tier}_monthly`)}
                </DefaultText>
                <DefaultText
                  fontFamily={fonts.primary}
                  fontSize="13px"
                  lineHeight="18px"
                >
                  {translate(`zero_risk_x_day_trial`, getUserFreeTrialDays())}
                </DefaultText>
                <DefaultText
                  fontFamily={fonts.primary}
                  fontSize="13px"
                  lineHeight="18px"
                >
                  {translate(`cancel_anytime`)}
                </DefaultText>
              </div>
              <DefaultText
                fontFamily={fonts.primary}
                fontSize="14px"
                lineHeight="24px"
                boldColor={palette.darkGray}
                boldFontFamily={fonts.primaryMedium}
                boldFontSize="24px"
                color={palette.orange}
                dangerouslySetInnerHTML={{
                  __html: `${translate(
                    'currency_at_symbol',
                    monthly?.discountPrice
                      ? monthly?.discountPrice
                      : monthly?.unitPrice,
                    '',
                  )}`,
                }}
                style={{
                  display: 'flex',
                  flex: 1,
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  padding: '8px 0 8px 16px',
                  borderLeft: `1px solid ${palette.lightGray50}`,
                }}
              />
              {monthly?.discountPercentNumber ? (
                <Text color={palette.orange} fontSize="14px" textAlign="center">
                  {translate(
                    'five_percent_discount',
                    monthly?.discountPercentNumber,
                  )}
                </Text>
              ) : null}
            </Mobile>
          </>
        )}
      </SwitchLabelLeft>
      <Desktop>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Switch
            data-testid="switch-checkout-page"
            className="plan-switch"
            onChange={(checked) => {
              if (checked) {
                onChangeValue(options[1])
              } else {
                onChangeValue(options[0])
              }
            }}
            checked={value.value === lifetime?.value}
            id="tier1-switch"
            offColor={palette.darkGray20}
            onColor={palette.darkGray20}
            offHandleColor={palette.orange}
            onHandleColor={palette.orange}
            handleDiameter={24}
            uncheckedIcon={false}
            checkedIcon={false}
            height={15}
            width={40}
            disabled={isLoading || isRecurlyLoading}
          />
        </div>
      </Desktop>
      <SwitchLabelRight
        data-testid="right-box"
        active={value.value === lifetime?.value}
        onClick={() => onChangeValue(options[1])}
      >
        {isCouponLoading ? (
          <WheelWrapper>
            <LoadingWheel width={24} height={24} />
          </WheelWrapper>
        ) : (
          <>
            <Desktop>
              <DefaultText
                data-testid="right-box-title"
                fontFamily={fonts.primaryMedium}
                fontSize="14px"
                lineHeight="24px"
                mobileFontSize="12px"
              >
                {translate(`dropdown_${lifetime?.tier}_lifetime`)}
              </DefaultText>
              <DefaultText
                data-testid="right-box-price"
                fontFamily={fonts.primary}
                fontSize="14px"
                lineHeight="24px"
                mobileFontSize="12px"
                boldColor={palette.darkGray}
                boldFontFamily={fonts.primaryMedium}
                boldFontSize="24px"
                color={palette.orange}
                dangerouslySetInnerHTML={{
                  __html: `${translate(
                    'currency_at_symbol',
                    lifetime?.discountPrice
                      ? lifetime?.discountPrice
                      : lifetime?.unitPrice,
                    '',
                  )}`,
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  margin: '8px 0',
                }}
              />
              {lifetime?.discountPercentNumber ? (
                <Text color={palette.orange} fontSize="14px" textAlign="center">
                  {translate(
                    'five_percent_discount',
                    lifetime?.discountPercentNumber,
                  )}
                </Text>
              ) : null}
              <DefaultText
                data-testid="right-box-description"
                fontFamily={fonts.primary}
                fontSize="13px"
                lineHeight="18px"
                dangerouslySetInnerHTML={{
                  __html: options[1]?.subscriptionDescription,
                }}
              />
            </Desktop>
            <Mobile>
              <div style={{ flex: 2 }}>
                <DefaultText
                  fontFamily={fonts.primaryMedium}
                  fontSize="14px"
                  lineHeight="24px"
                >
                  {translate(`dropdown_${monthly?.tier}_lifetime`)}
                </DefaultText>
                {!isCouponLoading && (
                  <DefaultText
                    fontFamily={fonts.primary}
                    fontSize="13px"
                    lineHeight="18px"
                    dangerouslySetInnerHTML={{
                      __html: options[1]?.subscriptionDescription,
                    }}
                  />
                )}
              </div>
              <DefaultText
                fontFamily={fonts.primary}
                fontSize="14px"
                lineHeight="24px"
                boldColor={palette.darkGray}
                boldFontFamily={fonts.primaryMedium}
                boldFontSize="24px"
                color={palette.orange}
                dangerouslySetInnerHTML={{
                  __html: `${translate(
                    'currency_at_symbol',
                    lifetime?.discountPrice
                      ? lifetime?.discountPrice
                      : lifetime?.unitPrice,
                    '',
                  )}`,
                }}
                style={{
                  display: 'flex',
                  flex: 1,
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  padding: '8px 0 8px 16px',
                  borderLeft: `1px solid ${palette.lightGray50}`,
                }}
              />
              {lifetime?.discountPercentNumber ? (
                <Text color={palette.orange} fontSize="14px" textAlign="center">
                  {translate(
                    'five_percent_discount',
                    lifetime?.discountPercentNumber,
                  )}
                </Text>
              ) : null}
            </Mobile>
          </>
        )}
      </SwitchLabelRight>
    </Switcher>
  )
}
